import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Event, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import Swal from 'sweetalert2';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from 'src/app/services/language.service';
import { ReservationsService } from 'src/app/services/reservations.service';

import * as feather from 'feather-icons';

@Component({
  selector: 'app-nav-user',
  templateUrl: './nav-user.component.html',
  styleUrls: ['./nav-user.component.scss'],
})
export class NavUserComponent implements OnInit, OnDestroy {
  public notInAuthModule: boolean = false;
  public currentLang: string = 'es';
  private languageSubscription?: Subscription;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const insideDropdownToggle = target.closest('[data-bs-toggle="dropdown"]');
    const insideDropdownMenu = target.closest('.dropdown-menu');
    const insideCollapseToggle = target.closest(
      '[data-bs-target="#navbarNavAuthCollapse"]',
    );
    const insideNavbarCollapse = target.closest('#navbarNavAuthCollapse');
    if (
      !insideDropdownToggle &&
      !insideDropdownMenu &&
      !insideCollapseToggle &&
      !insideNavbarCollapse
    ) {
      this.closeNavMenus();
    }
  }

  /** Profile photo from localStorage (updated after avatar upload / getCurrentUser). */
  get avatarUrl(): string {
    try {
      const current = JSON.parse(localStorage.getItem('current') || '{}');
      return String(current?.avatar || current?.photoUrl || '').trim();
    } catch {
      return '';
    }
  }

  constructor(
    public readonly isAuth: AuthService,
    readonly router: Router,
    private readonly _translate: TranslateService,
    private readonly languageService: LanguageService,
    public readonly reservationsService: ReservationsService,
  ) {
    this.currentLang = this.languageService.currentLanguage;
    router.events
      .pipe(
        filter(
          (e: Event | RouterEvent): e is RouterEvent =>
            e instanceof RouterEvent,
        ),
      )
      .subscribe((val: RouterEvent) => {
        this.notInAuthModule = val.url?.includes('auth') ? false : true;
        feather.replace();
        if (val instanceof NavigationEnd) {
          this.closeNavMenus();
        }
      });
  }

  private closeNavMenus(): void {
    document
      .querySelectorAll('.navbar-collapse')
      .forEach((el) => el.classList.remove('show'));
    document
      .querySelectorAll('.dropdown-menu.show')
      .forEach((el) => el.classList.remove('show'));
    document
      .querySelectorAll('[data-bs-toggle="dropdown"][aria-expanded="true"]')
      .forEach((el) => el.setAttribute('aria-expanded', 'false'));
  }

  ngOnInit(): void {
    feather.replace();
    this.languageSubscription = this.languageService.currentLanguage$.subscribe(
      (lang) => {
        this.currentLang = lang;
      },
    );
  }

  ngOnDestroy(): void {
    this.languageSubscription?.unsubscribe();
  }

  setLanguage(lang: string): void {
    void this.languageService.setLanguage(lang);
  }

  async logout() {
    const result = await Swal.fire({
      title: this._translate.instant('NAV_USER.LOGOUT_CONFIRM_TITLE'),
      text: this._translate.instant('NAV_USER.LOGOUT_CONFIRM_TEXT'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#00C08B',
      cancelButtonColor: '#d33',
      confirmButtonText: this._translate.instant('NAV_USER.LOGOUT_CONFIRM_YES'),
      cancelButtonText: this._translate.instant('MODAL.BUTTON_CANCEL'),
      background: '#0B2634',
      color: '#fff',
    });

    if (result.isConfirmed) {
      document
        .getElementsByClassName('navbar-collapse')[0]
        ?.classList.remove('show');
      localStorage.removeItem('token');
      localStorage.removeItem('current');
      localStorage.removeItem('reservations');
      localStorage.removeItem('branch');
      localStorage.removeItem('branchId');
      localStorage.removeItem('branches');
      this.reservationsService.setActiveNoticeCollapsed(false);
      this.isAuth.logout();
      void this.router.navigate(['/']);
    }
  }
}
